// Lightweight auth context module — exports only the React context, the
// type, the default state, and the `useAuth` hook. Critically, this file
// does NOT import `@supabase/supabase-js` (or any module that does), so
// components that only need `useAuth` (Header, ProtectedRoute, etc.) do
// not pull supabase-js into the initial bundle.
//
// The full `AuthProvider` lives in `AuthContext.tsx` and is loaded lazily
// by `DeferredAuthOnly` after idle.
import { createContext, useContext } from "react";
import type { Session, User } from "@supabase/supabase-js";

export type AppRole = "admin" | "user" | "premium";

export interface SignUpMetadata {
  first_name?: string;
  last_name?: string;
  country?: string;
  bird_categories?: string[];
}

export interface AuthContextType {
  session: Session | null;
  user: User | null;
  isAdmin: boolean;
  roles: AppRole[];
  hasRole: (role: AppRole) => boolean;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signUp: (
    email: string,
    password: string,
    fullName?: string,
    metadata?: SignUpMetadata,
    emailLocale?: "cs" | "en",
  ) => Promise<{ error: Error | null }>;
  updateUserEmail: (
    newEmail: string,
    emailLocale?: "cs" | "en",
  ) => Promise<{ error: Error | null }>;
  setEmailLocale: (
    emailLocale: "cs" | "en",
  ) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Safe defaults used while AuthProvider has not yet mounted (it is deferred
// via requestIdleCallback so supabase-js stays off the LCP path).
export const defaultAuthState: AuthContextType = {
  session: null,
  user: null,
  isAdmin: false,
  roles: [],
  hasRole: () => false,
  loading: true,
  signIn: async () => ({ error: new Error("Auth provider not ready") }),
  signUp: async () => ({ error: new Error("Auth provider not ready") }),
  updateUserEmail: async () => ({ error: new Error("Auth provider not ready") }),
  setEmailLocale: async () => ({ error: new Error("Auth provider not ready") }),
  signOut: async () => {},
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  return context ?? defaultAuthState;
};
